import cn from 'classnames';
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { CircularProgress } from '@mui/material';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { DropdownChevronDownSvg } from 'apps/sigma2-ui/src/components/svg/DropdownChevronDown';
import { DropdownChevronUpSvg } from 'apps/sigma2-ui/src/components/svg/DropdownChevronUp';
import { generateReportFilename } from 'libs/end-to-end/src/generate-report';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { mapCustomVizRequestToAnalyticsEventProperties } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { PendingReportInfo } from 'apps/sigma2-ui/src/reports/hooks/types';
import { ReportToastInteractionEvent } from 'libs/end-to-end/src/analytics/ReportToastInteractionEvent';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { useIsUserAnalysisEnabled } from 'apps/sigma2-ui/src/components/ui/useUserAnalysis';
import { useLocalStorage } from 'apps/sigma2-ui/src/lib/hooks/useLocalStorage';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { UserAnalysisInformation } from 'libs/end-to-end/src/types/user-analysis/user-analysis-details';
import { UserAnalysisInteractionEvent } from 'libs/end-to-end/src/analytics/UserAnalysisInteractionEvent';
import { NewDashboardChartInteractionEvent } from 'libs/end-to-end/src/analytics/NewDashboardChartInteractionEvent';
import { useReports } from 'apps/sigma2-ui/src/reports/hooks/useReports';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useUserAnalysisSignaling } from 'apps/sigma2-ui/src/state/user-analysis';
import { z } from 'zod';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { DashboardChartToBeCreated } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

export const UserAnalysesOverlay: React.FC<{
  /**
   * Required prop if the page has a user analysis on it
   */
  onUserAnalysisCancel: (
    customVizRequestToCancel: CustomVizRequest | null,
  ) => void | null;
  children?: React.ReactNode;
}> = ({ onUserAnalysisCancel, children }) => {
  const {
    currentUserAnalyses,
    onDeleteUserAnalysis: onDeleteUserAnalysisFromSignaling,
  } = useUserAnalysisSignaling();

  const { myObservation } = useObservation();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const { pendingReports } = useReports();

  const pendingReportsForCurrentObservation = useMemo(() => {
    return pendingReports.filter(
      (report) =>
        report.organizationId === selectedOrganizationId &&
        report.databaseId === selectedDatabaseId &&
        report.databaseRevisionId === selectedDatabaseRevisionId,
    );
  }, [
    pendingReports,
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  ]);

  const { newDashboardChartList } = useDashboards();

  const navigate = useNavigateSafe();

  // Because the UserAnalysesOvervlay renders in the MainLayout, it will be
  // mounted and unmounted every time the user navigates to a new page. This
  // means that we need to keep track of which analyses are already loaded for the
  // user so that we don't show the animation for them again. To avoid rerenders
  // of the UserAnalysesOverlay, we use a ref to store the initial analysis ids.
  const initialAnalysisIdsRef = useRef<string[]>([]);
  const [didInitialLoad, setDidInitialLoad] = useState(false);
  if (!didInitialLoad && currentUserAnalyses) {
    initialAnalysisIdsRef.current = currentUserAnalyses.map(
      (analysis) => analysis.id,
    );
    setDidInitialLoad(true);
  }

  const analysesToDisplay = useMemo(() => {
    if (currentUserAnalyses === null) {
      return [];
    }

    return currentUserAnalyses;
  }, [currentUserAnalyses]);

  const analysesToDisplayInQueue = useMemo(() => {
    if (currentUserAnalyses === null) {
      return [];
    }

    // reverse the analyses so that the most recent analysis is displayed first
    const currentUserAnalysesCopy = [...currentUserAnalyses];
    currentUserAnalysesCopy.reverse();
    return currentUserAnalysesCopy;
  }, [currentUserAnalyses]);

  const currentNumberOfVisibleToasts = useMemo(() => {
    return analysesToDisplay.length;
  }, [analysesToDisplay]);

  const onDeleteUserAnalysis = useCallback(
    ({
      analysisId,
    }: {
      analysisId: string;
      customVizRequestToCancel: CustomVizRequest;
    }) => {
      onDeleteUserAnalysisFromSignaling({
        analysisId,
      });
    },
    [onDeleteUserAnalysisFromSignaling],
  );

  const isUserAnalysisEnabled = useIsUserAnalysisEnabled();

  const { featureFlags } = useUser();

  const isShowingCustomVizCurrentStatusEnabled = useMemo(() => {
    return featureFlags?.showStatusForCustomVizLoading ?? false;
  }, [featureFlags]);

  const [isUserAnalysisQueueExpanded, setIsUserAnalysisQueueExpanded] =
    useLocalStorage({
      defaultValue: false,
      key: 'user-analysis-queue-expanded',
      schema: z.boolean(),
    });

  const queueLength =
    analysesToDisplay.length +
    pendingReportsForCurrentObservation.length +
    newDashboardChartList.length;

  if (!isUserAnalysisEnabled || !myObservation) {
    return null;
  }

  return (
    <div className="fixed end-4 bottom-4 z-50">
      <div className="flex flex-col gap-2">
        {children}
        <div
          id="user-analysis-overlay"
          className={cn(
            'max-h-[50vh] w-[384px] overflow-y-scroll drop-shadow-2xl rounded-b-lg',
          )}
        >
          <Box
            as={'div'}
            display={'flex'}
            flexDirection={'col'}
            h={'full'}
            w={'full'}
            bg={'transparent'}
          >
            {queueLength > 0 && (
              <Box position={'sticky'} top={'0'}>
                <UserAnalysisQueueHeader
                  itemsInQueue={queueLength}
                  isUserAnalysisQueueExpanded={isUserAnalysisQueueExpanded}
                  setIsUserAnalysisQueueExpanded={
                    setIsUserAnalysisQueueExpanded
                  }
                />
              </Box>
            )}
            {isUserAnalysisQueueExpanded && (
              <>
                {analysesToDisplayInQueue.map((analysis) => (
                  <Box key={analysis.id}>
                    <UserAnalysesOverlayCardInQueue
                      analysis={analysis}
                      onDeleteUserAnalysis={onDeleteUserAnalysis}
                      onUserAnalysisCancel={onUserAnalysisCancel}
                      currentNumberOfVisibleToasts={
                        currentNumberOfVisibleToasts
                      }
                      isShowingCustomVizCurrentStatusEnabled={
                        isShowingCustomVizCurrentStatusEnabled
                      }
                      myObservation={myObservation}
                      navigate={navigate}
                    />

                    <Box h={'h-[1px]'} w={'full'} bg={'gray-200'} />
                  </Box>
                ))}

                {pendingReportsForCurrentObservation.map((pendingReport) => (
                  <Box key={pendingReport.reportId}>
                    <PendingReportToastCardInQueue
                      pendingReport={pendingReport}
                      currentNumberOfVisibleToasts={
                        currentNumberOfVisibleToasts
                      }
                    />
                    <Box h={'h-[1px]'} w={'full'} bg={'gray-200'} />
                  </Box>
                ))}

                {newDashboardChartList.map((chart) => (
                  <Box key={chart.chart.id}>
                    <NewDashboardChartToastCardInQueue
                      chart={chart}
                      currentNumberOfVisibleToasts={
                        currentNumberOfVisibleToasts
                      }
                      navigate={navigate}
                    />
                    <Box h={'h-[1px]'} w={'full'} bg={'gray-200'} />
                  </Box>
                ))}
              </>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};

export const UserAnalysisQueueHeader = ({
  itemsInQueue,
  isUserAnalysisQueueExpanded,
  setIsUserAnalysisQueueExpanded,
}: {
  itemsInQueue: number;
  isUserAnalysisQueueExpanded: boolean;
  setIsUserAnalysisQueueExpanded: SetterWithReducer<boolean>;
}) => {
  const language = useLanguage();

  return (
    <TouchTarget
      className={'pointer-events-auto'}
      content={() => {
        return (
          <Box
            as={'div'}
            bg={'royal-1000'}
            w={'full'}
            display={'flex'}
            justifyContent={'between'}
            alignSelf={'center'}
            p={'xs'}
            tl={'lg'}
            tr={'lg'}
            bl={isUserAnalysisQueueExpanded ? 'none' : 'lg'}
            br={isUserAnalysisQueueExpanded ? 'none' : 'lg'}
          >
            <Spacing size={'sm'} direction={'horizontal'}>
              <S2Typography
                variant={'headline-6-alt'}
                translatedContent={language.dangerousUserInput({
                  value: 'Your Queue',
                })}
                textColorVariant={'white'}
              />

              <Box
                as={'div'}
                bg={'royal-800'}
                rounded={'full'}
                w={'w-[24px]'}
                h={'h-[24px]'}
                display={'flex'}
                justifyContent={'center'}
              >
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={language.dangerousUserInput({
                    value: `${itemsInQueue}`,
                  })}
                  textColorVariant={'white'}
                />
              </Box>
            </Spacing>

            {isUserAnalysisQueueExpanded ? (
              <DropdownChevronDownSvg
                wantsColor={'#FFFFFF'}
                svg={{ className: 'w-[24px] h-[24px] block' }}
              />
            ) : (
              <DropdownChevronUpSvg
                wantsColor={'#FFFFFF'}
                svg={{ className: 'w-[24px] h-[24px] block' }}
              />
            )}
          </Box>
        );
      }}
      behavior={{
        type: 'onClick',
        onClick: () => {
          setIsUserAnalysisQueueExpanded((prev) => !prev);
        },
      }}
    />
  );
};

export const UserAnalysesOverlayCardInQueue = ({
  analysis,
  onDeleteUserAnalysis,
  onUserAnalysisCancel,
  currentNumberOfVisibleToasts,
  isShowingCustomVizCurrentStatusEnabled,
  myObservation,
  navigate,
}: {
  analysis: UserAnalysisInformation;
  onDeleteUserAnalysis: (params: { analysisId: string }) => void;
  /**
   * Required if the page has a custom viz on it
   */
  onUserAnalysisCancel: Nullable<
    (customVizRequestToCancel: Nullable<CustomVizRequest>) => void
  >;
  currentNumberOfVisibleToasts: number;
  isShowingCustomVizCurrentStatusEnabled: boolean;
  myObservation: MyObservation;
  navigate: (url: string) => void;
}) => {
  const analytics = useAnalytics();
  const language = useLanguage();

  const metricDefinitions = useMemo(() => {
    return myObservation.focusAreas.flatMap((focusArea) => {
      return focusArea.metrics;
    });
  }, [myObservation]);

  const onSuccessfulClick = useCallback(() => {
    analytics.trackEvent(
      mapUserAnalysisInteractionEvent({
        information: analysis,
        interaction: 'See Results',
        numberOfVisibleToasts: currentNumberOfVisibleToasts,
      }),
    );
    onUserAnalysisCancel?.(null);

    navigate(analysis.shareLink);
  }, [
    analysis,
    analytics,
    currentNumberOfVisibleToasts,
    navigate,
    onUserAnalysisCancel,
  ]);

  const bodyComponent = useMemo(() => {
    if (analysis.status === 'pending') {
      return (
        <>
          <S2Typography
            variant={'body-7'}
            translatedContent={language.userAnalysisOverlayTimeRemaining({
              estimatedTimeRemainingInSeconds:
                analysis.estimatedTimeRemaningInSeconds,
            })}
            textColorVariant={'gray-800'}
          />
          {isShowingCustomVizCurrentStatusEnabled && analysis.currentStep && (
            <S2Typography
              variant={'body-7'}
              translatedContent={language.customVizProgressStep({
                step: analysis.currentStep,
              })}
              textColorVariant={'gray-800'}
            />
          )}
        </>
      );
    } else if (
      analysis.status === 'succeeded' ||
      analysis.status === 'not-enough-data'
    ) {
      return <></>;
    } else if (analysis.status === 'failed') {
      return (
        <S2Typography
          variant={'body-7'}
          translatedContent={language.userAnalysisOverlayUnableToLoadDescription()}
          textColorVariant={'gray-800'}
        />
      );
    } else {
      throw guardExhaustive(analysis.status);
    }
  }, [
    analysis.currentStep,
    analysis.estimatedTimeRemaningInSeconds,
    analysis.status,
    isShowingCustomVizCurrentStatusEnabled,
    language,
  ]);

  const leftChevronComponent = useMemo(() => {
    if (
      analysis.status === 'succeeded' ||
      analysis.status === 'not-enough-data'
    ) {
      return <IconCheckmarkWithGreenBackground24pxSvg />;
    } else if (analysis.status === 'failed') {
      return <IconExclamationWithRedBackground24pxSvg />;
    } else if (analysis.status === 'pending') {
      return <CircularProgress size={20} />;
    } else {
      throw guardExhaustive(analysis.status);
    }
  }, [analysis]);

  /**
   * Right chevron displays either "Cancel" or the close icon, which acts like
   * a cancellation. We want to not only delete the toast in this case with
   * onDeleteUserAnalysis, but also call the onUserAnalysisCancel method to
   * reset to the last successful viz request or reset the dropdowns to the
   * initial state
   */
  const onClickRightChevron = useCallback(() => {
    analytics.trackEvent(
      mapUserAnalysisInteractionEvent({
        information: analysis,
        interaction: 'Close',
        numberOfVisibleToasts: currentNumberOfVisibleToasts,
      }),
    );
    onUserAnalysisCancel?.(analysis.customVizRequest);

    onDeleteUserAnalysis({
      analysisId: analysis.id,
    });
  }, [
    analysis,
    analytics,
    currentNumberOfVisibleToasts,
    onDeleteUserAnalysis,
    onUserAnalysisCancel,
  ]);

  const rightChevronComponent = useMemo(() => {
    return (
      <TouchTarget
        className="close-notification-button"
        content={() => {
          return (
            <div data-testid="right-chevron">
              {' '}
              <IconCloseSvg svg={{ className: 'text-gray-800' }} />{' '}
            </div>
          );
        }}
        behavior={{
          type: 'onClick',
          onClick: () => {
            onClickRightChevron();
          },
        }}
      />
    );
  }, [onClickRightChevron]);

  const secondaryTranslatedContent = useMemo(() => {
    return language.savedAnalysisCardBody({
      myObservation,
      request: analysis.customVizRequest,
    });
  }, [analysis, myObservation, language]);

  const clickBehavior: TouchTargetBehavior | null = useMemo(() => {
    if (analysis.status === 'succeeded') {
      return {
        type: 'onClick',
        onClick: () => {
          onSuccessfulClick();
        },
      };
    }

    return null;
  }, [analysis.status, onSuccessfulClick]);

  return (
    <ToastItemInQueue
      bodyComponent={bodyComponent}
      leftChevronComponent={leftChevronComponent}
      rightChevronComponent={rightChevronComponent}
      secondaryTranslatedContent={secondaryTranslatedContent}
      titleTranslatedContent={language.userAnalysisOverlayTitle({
        analysis,
        metricDefinitions,
      })}
      clickBehavior={clickBehavior}
    />
  );
};

export const PendingReportToastCardInQueue = ({
  pendingReport,
  currentNumberOfVisibleToasts,
}: {
  pendingReport: PendingReportInfo;
  currentNumberOfVisibleToasts: number;
}) => {
  const { removePendingReport } = useReports();

  const analytics = useAnalytics();
  const language = useLanguage();

  const onSuccessfulClick = useCallback(() => {
    analytics.trackEvent(
      mapPendingReportInteractionEvent({
        pendingReport,
        interaction: 'Download',
        numberOfVisibleToasts: currentNumberOfVisibleToasts,
      }),
    );

    removePendingReport(pendingReport.reportId);

    downloadUrlAsFilename({
      url: pendingReport.presignedUrl ?? '',
      filename: generateReportFilename(pendingReport.reportId),
    });
  }, [
    analytics,
    currentNumberOfVisibleToasts,
    pendingReport,
    removePendingReport,
  ]);

  const bodyComponent = useMemo(() => {
    if (
      pendingReport.status === 'pending' ||
      pendingReport.status === 'processing'
    ) {
      return (
        <S2Typography
          variant={'body-7'}
          translatedContent={language.pendingReportToastLoading()}
          textColorVariant={'gray-800'}
        />
      );
    } else if (pendingReport.status === 'success') {
      return (
        <button
          onClick={() => {
            onSuccessfulClick();
          }}
        >
          <S2Typography
            variant={'body-7'}
            translatedContent={language.pendingReportToastDownload()}
            textColorVariant={'gray-800'}
          />
        </button>
      );
    } else if (pendingReport.status === 'failure') {
      return (
        <S2Typography
          variant={'body-7'}
          translatedContent={language.pendingReportToastFailed()}
          textColorVariant={'gray-800'}
        />
      );
    } else {
      throw guardExhaustive(pendingReport.status);
    }
  }, [language, onSuccessfulClick, pendingReport.status]);

  const leftChevronComponent = useMemo(() => {
    if (pendingReport.status === 'success') {
      return <IconCheckmarkWithGreenBackground24pxSvg />;
    } else if (pendingReport.status === 'failure') {
      return <IconExclamationWithRedBackground24pxSvg />;
    } else if (
      pendingReport.status === 'pending' ||
      pendingReport.status === 'processing'
    ) {
      return <CircularProgress size={20} />;
    } else {
      throw guardExhaustive(pendingReport.status);
    }
  }, [pendingReport]);

  const onClickRightChevron = useCallback(() => {
    analytics.trackEvent(
      mapPendingReportInteractionEvent({
        pendingReport,
        interaction: 'Close',
        numberOfVisibleToasts: currentNumberOfVisibleToasts,
      }),
    );

    removePendingReport(pendingReport.reportId);
  }, [
    analytics,
    currentNumberOfVisibleToasts,
    pendingReport,
    removePendingReport,
  ]);

  const rightChevronComponent = useMemo(() => {
    return (
      <TouchTarget
        content={() => {
          return (
            <div data-testid="right-chevron">
              {' '}
              <IconCloseSvg svg={{ className: 'text-gray-800' }} />{' '}
            </div>
          );
        }}
        behavior={{
          type: 'onClick',
          onClick: () => {
            onClickRightChevron();
          },
        }}
      />
    );
  }, [onClickRightChevron]);

  const clickBehavior: TouchTargetBehavior | null = useMemo(() => {
    if (pendingReport.status === 'success') {
      return {
        onClick: () => {
          onSuccessfulClick();
        },
        type: 'onClick',
      };
    }

    return null;
  }, [pendingReport.status, onSuccessfulClick]);

  return (
    <ToastItemInQueue
      leftChevronComponent={leftChevronComponent}
      rightChevronComponent={rightChevronComponent}
      bodyComponent={bodyComponent}
      secondaryTranslatedContent={''}
      clickBehavior={clickBehavior}
      titleTranslatedContent={language.pendingReportToastTitle()}
    />
  );
};

export const NewDashboardChartToastCardInQueue = ({
  chart,
  currentNumberOfVisibleToasts,
  navigate,
}: {
  chart: DashboardChartToBeCreated;
  currentNumberOfVisibleToasts: number;
  navigate: (url: string) => void;
}) => {
  const { removeNewDashboardChart, dashboardsList } = useDashboards();

  const analytics = useAnalytics();
  const language = useLanguage();

  const dashboard = useMemo(() => {
    return dashboardsList?.find(
      (dashboard) => dashboard.id === chart.chart.dashboardId,
    );
  }, [dashboardsList, chart.chart.dashboardId]);

  const onSuccessfulClick = useCallback(() => {
    if (chart.chart.id) {
      analytics.trackEvent(
        mapNewDashboardChartInteractionEvent({
          chartId: chart.chart.id,
          chart,
          interaction: 'Visit',
          numberOfVisibleToasts: currentNumberOfVisibleToasts,
        }),
      );
    }

    if (chart.state === 'success') {
      if (chart.chart.id) {
        removeNewDashboardChart({ draftId: chart.chart.draftId });
      }

      navigate(`/dashboards/${chart.chart.dashboardId}`);
    }
  }, [
    chart,
    removeNewDashboardChart,
    navigate,
    analytics,
    currentNumberOfVisibleToasts,
  ]);

  const bodyComponent = useMemo(() => {
    if (chart.state === 'pending') {
      return (
        <S2Typography
          variant={'body-7'}
          translatedContent={language.addingChartToDashboard()}
          textColorVariant={'gray-800'}
        />
      );
    } else if (chart.state === 'success') {
      return (
        <button onClick={onSuccessfulClick} className="text-left">
          <S2Typography
            variant={'body-7'}
            translatedContent={language.savedToDashboardHeader({
              chart: chart.chart,
              dashboard,
            })}
            textColorVariant={'gray-800'}
          />
        </button>
      );
    } else if (chart.state === 'failure') {
      return (
        <S2Typography
          variant={'body-7'}
          translatedContent={language.failedToAddChartToDashboard()}
          textColorVariant={'gray-800'}
        />
      );
    } else {
      throw guardExhaustive(chart.state);
    }
  }, [language, onSuccessfulClick, chart.chart, dashboard, chart.state]);

  const leftChevronComponent = useMemo(() => {
    if (chart.state === 'success') {
      return <IconCheckmarkWithGreenBackground24pxSvg />;
    } else if (chart.state === 'failure') {
      return <IconExclamationWithRedBackground24pxSvg />;
    } else if (chart.state === 'pending') {
      return <CircularProgress size={20} />;
    } else {
      throw guardExhaustive(chart.state);
    }
  }, [chart.state]);

  const onClickRightChevron = useCallback(() => {
    if (chart.chart.id) {
      analytics.trackEvent(
        mapNewDashboardChartInteractionEvent({
          chartId: chart.chart.id,
          chart,
          interaction: 'Close',
          numberOfVisibleToasts: currentNumberOfVisibleToasts,
        }),
      );

      removeNewDashboardChart({ draftId: chart.chart.draftId });
    }
  }, [chart, removeNewDashboardChart, analytics, currentNumberOfVisibleToasts]);

  const rightChevronComponent = useMemo(() => {
    return (
      <TouchTarget
        content={() => {
          return (
            <div data-testid="right-chevron">
              {' '}
              <IconCloseSvg svg={{ className: 'text-gray-800' }} />{' '}
            </div>
          );
        }}
        behavior={{
          type: 'onClick',
          onClick: () => {
            onClickRightChevron();
          },
        }}
      />
    );
  }, [onClickRightChevron]);

  const clickBehavior: TouchTargetBehavior | null = useMemo(() => {
    return {
      onClick: () => {
        onSuccessfulClick();
      },
      type: 'onClick',
    };
  }, [onSuccessfulClick]);

  return (
    <ToastItemInQueue
      leftChevronComponent={leftChevronComponent}
      rightChevronComponent={rightChevronComponent}
      bodyComponent={bodyComponent}
      secondaryTranslatedContent={''}
      clickBehavior={clickBehavior}
      titleTranslatedContent={language.visitDashboard()}
    />
  );
};

export const downloadUrlAsFilename = ({
  url,
  filename,
}: {
  url: string;
  filename: string;
}) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const IconCheckmarkWithGreenBackground24pxGroup: React.FC<
  SvgProps
> = () => {
  return (
    <g>
      <rect x="2" y="2" width="20" height="20" rx="10" fill="#379A15" />
      <path
        d="M16.3893 9L10.7964 15.292L8 13.1946"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconCheckmarkWithGreenBackground24pxSvg: React.FC<SvgProps> = (
  props,
) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconCheckmarkWithGreenBackground24pxGroup {...props} />
    </svg>
  );
};

export const IconExclamationWithRedBackground24pxGroup: React.FC<
  SvgProps
> = () => {
  return (
    <g>
      <path
        d="M22 12C22 13.3132 21.7413 14.6136 21.2388 15.8268C20.7362 17.0401 19.9997 18.1425 19.0711 19.0711C18.1425 19.9997 17.0401 20.7362 15.8268 21.2388C14.6136 21.7413 13.3132 22 12 22C10.6868 22 9.38642 21.7413 8.17317 21.2388C6.95991 20.7362 5.85752 19.9997 4.92893 19.0711C4.00035 18.1425 3.26375 17.0401 2.7612 15.8268C2.25866 14.6136 2 13.3132 2 12C2 9.34784 3.05357 6.8043 4.92893 4.92893C6.8043 3.05357 9.34784 2 12 2C14.6522 2 17.1957 3.05357 19.0711 4.92893C20.9464 6.8043 22 9.34784 22 12Z"
        fill="#D41515"
      />
      <path
        d="M12 8V12M12 16H12.01"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconExclamationWithRedBackground24pxSvg: React.FC<SvgProps> = (
  props,
) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconExclamationWithRedBackground24pxGroup {...props} />
    </svg>
  );
};

export const mapPendingReportInteractionEvent = ({
  pendingReport,
  interaction,
  numberOfVisibleToasts,
}: {
  pendingReport: PendingReportInfo;
  interaction: 'Close' | 'Download' | 'Try Again';
  numberOfVisibleToasts: number;
}): ReportToastInteractionEvent => {
  let state: 'Pending' | 'Processing' | 'Failure' | 'Success';
  if (pendingReport.status === 'pending') {
    state = 'Pending';
  } else if (pendingReport.status === 'processing') {
    state = 'Processing';
  } else if (pendingReport.status === 'failure') {
    state = 'Failure';
  } else if (pendingReport.status === 'success') {
    state = 'Success';
  } else {
    throw guardExhaustive(pendingReport.status);
  }
  return {
    eventName: 'Report Toast Interaction',
    eventProperties: {
      'Report ID': pendingReport.reportId,
      'Toast State': state,
      'Toast Interaction': interaction,
      'Number of Visible Toasts': numberOfVisibleToasts,
    },
  };
};

export const mapUserAnalysisInteractionEvent = ({
  information,
  interaction,
  numberOfVisibleToasts,
}: {
  information: UserAnalysisInformation;
  interaction: 'Close' | 'See Results' | 'Try Again';
  numberOfVisibleToasts: number;
}): UserAnalysisInteractionEvent => {
  let state: 'Success' | 'Failed' | 'In Progress' | 'Not Enough Data';
  if (information.status === 'succeeded') {
    state = 'Success';
  } else if (information.status === 'failed') {
    state = 'Failed';
  } else if (information.status === 'pending') {
    state = 'In Progress';
  } else if (information.status === 'not-enough-data') {
    state = 'Not Enough Data';
  } else {
    throw guardExhaustive(information.status);
  }
  return {
    eventName: 'User Analysis Toast Interaction',
    eventProperties: {
      ...mapCustomVizRequestToAnalyticsEventProperties({
        request: information.customVizRequest,
      }),
      'Toast State': state,
      'Toast Interaction': interaction,
      'Number of Visible Toasts': numberOfVisibleToasts,
    },
  };
};

export const mapNewDashboardChartInteractionEvent = ({
  chartId,
  chart,
  interaction,
  numberOfVisibleToasts,
}: {
  chartId: string;
  chart: DashboardChartToBeCreated;
  interaction: 'Close' | 'Visit' | 'Try Again';
  numberOfVisibleToasts: number;
}): NewDashboardChartInteractionEvent => {
  let state: 'Pending' | 'Failure' | 'Success';
  if (chart.state === 'pending') {
    state = 'Pending';
  } else if (chart.state === 'failure') {
    state = 'Failure';
  } else if (chart.state === 'success') {
    state = 'Success';
  } else {
    throw guardExhaustive(chart.state);
  }
  return {
    eventName: 'New Dashboard Chart Toast Interaction',
    eventProperties: {
      'Chart ID': chartId,
      'Toast State': state,
      'Toast Interaction': interaction,
      'Number of Visible Toasts': numberOfVisibleToasts,
    },
  };
};

export const ToastItemInQueue = ({
  bodyComponent,
  leftChevronComponent,
  rightChevronComponent,
  secondaryTranslatedContent,
  titleTranslatedContent,
  clickBehavior,
}: {
  bodyComponent: React.ReactNode;
  leftChevronComponent: React.ReactNode;
  rightChevronComponent: React.ReactNode;
  secondaryTranslatedContent: TranslatedContent;
  titleTranslatedContent: TranslatedContent;
  clickBehavior: TouchTargetBehavior | null;
}) => {
  const cardUi = useMemo(() => {
    return (
      <div className="w-full pointer-events-auto p-2">
        <div data-testid="user-analysis-toast">
          <Spacing
            size={''}
            direction={'horizontal'}
            className={'items-center px-1'}
          >
            <Box as={'div'}>{leftChevronComponent}</Box>
            <Spacing size={'xs'} direction={'vertical'} className={'flex-grow'}>
              <S2Typography
                variant="headline-7-alt"
                translatedContent={titleTranslatedContent}
                textColorVariant={'gray-800'}
              />
              <div className="flex-grow text-xxs leading-normal">
                {convertTranslatedContentToReactNode(
                  secondaryTranslatedContent,
                )}
              </div>
              <div className={'flex-grow-0'}>{bodyComponent}</div>
            </Spacing>
            {rightChevronComponent}
          </Spacing>
        </div>
      </div>
    );
  }, [
    leftChevronComponent,
    titleTranslatedContent,
    secondaryTranslatedContent,
    bodyComponent,
    rightChevronComponent,
  ]);

  return useMemo(() => {
    if (!clickBehavior) {
      return (
        <Box as={'div'} bg={'white'}>
          {cardUi}
        </Box>
      );
    }

    return (
      <TouchTarget
        content={({ hovered }) => {
          return (
            <Box as={'div'} bg={hovered ? 'royal-100' : 'white'}>
              {cardUi}
            </Box>
          );
        }}
        behavior={clickBehavior}
      />
    );
  }, [clickBehavior, cardUi]);
};
